import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";
import { 
  FaServer, 
  FaDatabase, 
  FaCloud,
  FaCode,
  FaMobile,
  FaCogs,
  FaShieldAlt,
  FaChartLine,
  FaBrain
} from "react-icons/fa";

function TechnicalSkills() {
  const coreCompetencies = [
    {
      title: "System Architecture & Design",
      description: "Expert in designing scalable, distributed systems and microservices architectures",
      skills: ["Microservices Architecture", "Event-Driven Design", "Domain-Driven Design", "CQRS", "API Design", "Distributed Systems"]
    },
    {
      title: "Cloud-Native Solutions",
      description: "Proficient in cloud platforms and containerization technologies",
      skills: ["AWS", "GCP", "Kubernetes", "Docker", "Serverless", "Auto-scaling"]
    },
    {
      title: "Performance & Scalability",
      description: "Specialized in optimizing systems for high performance and scalability",
      skills: ["Caching", "Load Balancing", "CDN", "Database Optimization", "Memory Management"]
    },
    {
      title: "Security & Compliance",
      description: "Expert in implementing secure, compliant enterprise solutions",
      skills: ["PCI DSS", "GDPR", "API Security", "Data Encryption", "OAuth2", "JWT"]
    },
    {
      title: "Monitoring & Observability",
      description: "Comprehensive monitoring and observability solutions",
      skills: ["ELK Stack", "Prometheus", "Grafana", "Distributed Tracing", "Log Aggregation"]
    }
  ];

  const skillCategories = [
    {
      title: "System Architecture",
      icon: <FaServer />,
      skills: [
        { name: "Microservices Architecture", level: "Expert", percentage: 98 },
        { name: "Event-Driven Architecture", level: "Expert", percentage: 95 },
        { name: "Domain-Driven Design", level: "Expert", percentage: 92 },
        { name: "CQRS & Event Sourcing", level: "Advanced", percentage: 88 },
        { name: "Distributed Systems", level: "Expert", percentage: 95 },
        { name: "High-Availability Design", level: "Expert", percentage: 93 },
        { name: "API Gateway Design", level: "Expert", percentage: 90 },
        { name: "Service Mesh", level: "Advanced", percentage: 85 }
      ]
    },
    {
      title: "Backend Development",
      icon: <FaCode />,
      skills: [
        { name: "Java Spring Boot", level: "Expert", percentage: 98 },
        { name: "Node.js & Express.js", level: "Expert", percentage: 95 },
        { name: "Nest.js Framework", level: "Expert", percentage: 92 },
        { name: "Python Flask/Django", level: "Advanced", percentage: 88 },
        { name: "RESTful APIs", level: "Expert", percentage: 98 },
        { name: "GraphQL", level: "Advanced", percentage: 85 },
        { name: "Microservices", level: "Expert", percentage: 95 },
        { name: "JWT & OAuth2", level: "Expert", percentage: 92 }
      ]
    },
    {
      title: "Cloud & DevOps",
      icon: <FaCloud />,
      skills: [
        { name: "Amazon AWS", level: "Expert", percentage: 95 },
        { name: "Google Cloud Platform", level: "Advanced", percentage: 88 },
        { name: "Docker", level: "Expert", percentage: 92 },
        { name: "Kubernetes", level: "Expert", percentage: 90 },
        { name: "Jenkins", level: "Advanced", percentage: 85 },
        { name: "GitHub Actions", level: "Advanced", percentage: 88 },
        { name: "Terraform", level: "Advanced", percentage: 82 },
        { name: "Prometheus & Grafana", level: "Expert", percentage: 90 }
      ]
    },
    {
      title: "Database & Data",
      icon: <FaDatabase />,
      skills: [
        { name: "PostgreSQL", level: "Expert", percentage: 95 },
        { name: "MySQL", level: "Advanced", percentage: 88 },
        { name: "Oracle Database", level: "Advanced", percentage: 85 },
        { name: "MongoDB", level: "Expert", percentage: 92 },
        { name: "Redis", level: "Expert", percentage: 90 },
        { name: "Apache Kafka", level: "Advanced", percentage: 85 },
        { name: "Elasticsearch", level: "Advanced", percentage: 82 },
        { name: "Data Architecture", level: "Expert", percentage: 93 }
      ]
    },
    {
      title: "Frontend Technologies",
      icon: <FaCode />,
      skills: [
        { name: "React.js", level: "Expert", percentage: 95 },
        { name: "TypeScript", level: "Expert", percentage: 92 },
        { name: "JavaScript", level: "Expert", percentage: 98 },
        { name: "Redux", level: "Advanced", percentage: 88 },
        { name: "Tailwind CSS", level: "Advanced", percentage: 90 },
        { name: "HTML5 & CSS3", level: "Expert", percentage: 95 },
        { name: "Responsive Design", level: "Expert", percentage: 95 },
        { name: "Mobile-First", level: "Advanced", percentage: 88 }
      ]
    },
    {
      title: "Performance & Scalability",
      icon: <FaChartLine />,
      skills: [
        { name: "System Optimization", level: "Expert", percentage: 95 },
        { name: "Load Balancing", level: "Expert", percentage: 92 },
        { name: "Caching Strategies", level: "Expert", percentage: 95 },
        { name: "CDN Implementation", level: "Advanced", percentage: 85 },
        { name: "Database Optimization", level: "Expert", percentage: 90 },
        { name: "Memory Optimization", level: "Advanced", percentage: 88 },
        { name: "Network Optimization", level: "Advanced", percentage: 85 },
        { name: "Throughput Optimization", level: "Expert", percentage: 90 }
      ]
    },
    {
      title: "Security & Compliance",
      icon: <FaShieldAlt />,
      skills: [
        { name: "Application Security", level: "Expert", percentage: 92 },
        { name: "API Security", level: "Expert", percentage: 90 },
        { name: "Data Encryption", level: "Advanced", percentage: 88 },
        { name: "OAuth2 & JWT", level: "Expert", percentage: 95 },
        { name: "PCI DSS Compliance", level: "Advanced", percentage: 85 },
        { name: "GDPR Compliance", level: "Advanced", percentage: 82 },
        { name: "Security Auditing", level: "Advanced", percentage: 85 },
        { name: "Penetration Testing", level: "Intermediate", percentage: 75 }
      ]
    },
    {
      title: "Mobile Development",
      icon: <FaMobile />,
      skills: [
        { name: "React Native", level: "Expert", percentage: 92 },
        { name: "Expo Framework", level: "Advanced", percentage: 88 },
        { name: "Cross-platform Development", level: "Expert", percentage: 90 },
        { name: "iOS & Android", level: "Advanced", percentage: 85 },
        { name: "Push Notifications", level: "Advanced", percentage: 88 },
        { name: "App Store Publishing", level: "Advanced", percentage: 85 },
        { name: "Mobile Optimization", level: "Expert", percentage: 90 },
        { name: "Offline Capabilities", level: "Advanced", percentage: 80 }
      ]
    },
    {
      title: "Monitoring & Observability",
      icon: <FaCogs />,
      skills: [
        { name: "ELK Stack", level: "Expert", percentage: 90 },
        { name: "Prometheus & Grafana", level: "Expert", percentage: 92 },
        { name: "Application Monitoring", level: "Expert", percentage: 95 },
        { name: "Distributed Tracing", level: "Advanced", percentage: 85 },
        { name: "Log Aggregation", level: "Expert", percentage: 88 },
        { name: "Alerting Systems", level: "Advanced", percentage: 85 },
        { name: "Metrics Collection", level: "Expert", percentage: 90 },
        { name: "System Health Monitoring", level: "Expert", percentage: 92 }
      ]
    }
  ];

  const ProgressBar = ({ percentage }) => {
    return (
      <div style={{
        width: "100%",
        height: "8px",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        borderRadius: "4px",
        overflow: "hidden",
        position: "relative"
      }}>
        <div style={{
          width: `${percentage}%`,
          height: "100%",
          background: "linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)",
          borderRadius: "4px",
          transition: "width 1s ease-in-out",
          position: "relative"
        }}>
          <div style={{
            position: "absolute",
            right: "0",
            top: "50%",
            transform: "translateY(-50%)",
            width: "12px",
            height: "12px",
            backgroundColor: "#38f9d7",
            borderRadius: "50%",
            boxShadow: "0 0 10px rgba(56, 249, 215, 0.5)"
          }} />
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="technical-skills-section" id="technical-skills">
      <Particle />
      <Container>
        <h1 className="project-heading">
          Technical <strong className="purple">Skills</strong>
        </h1>
        <p style={{ color: "white", textAlign: "center", marginBottom: "40px", fontSize: "1.1em" }}>
          Comprehensive expertise across system architecture, cloud platforms, and enterprise solutions with focus on scalability, performance, and reliability.
        </p>

        {/* Core Competencies Section */}
        <div style={{ marginBottom: "50px" }}>
          <h2 style={{ 
            color: "#c770f0", 
            textAlign: "center", 
            marginBottom: "30px", 
            fontSize: "1.8em",
            fontWeight: "600"
          }}>
            <FaBrain style={{ marginRight: "10px" }} />
            Core Competencies
          </h2>
          <Row>
            {coreCompetencies.map((competency, index) => (
              <Col md={6} lg={4} key={index} style={{ marginBottom: "20px" }}>
                <div style={{
                  backgroundColor: "rgba(199, 112, 240, 0.1)",
                  border: "1px solid rgba(199, 112, 240, 0.2)",
                  borderRadius: "15px",
                  padding: "25px",
                  height: "100%",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-3px)";
                  e.target.style.boxShadow = "0 8px 25px rgba(199, 112, 240, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}>
                  <h4 style={{
                    color: "#c770f0",
                    fontSize: "1.2em",
                    fontWeight: "600",
                    marginBottom: "15px"
                  }}>
                    {competency.title}
                  </h4>
                  <p style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "0.9em",
                    marginBottom: "15px",
                    lineHeight: "1.5"
                  }}>
                    {competency.description}
                  </p>
                  <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px"
                  }}>
                    {competency.skills.map((skill, skillIndex) => (
                      <span key={skillIndex} style={{
                        backgroundColor: "rgba(56, 249, 215, 0.1)",
                        color: "#38f9d7",
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "0.75em",
                        border: "1px solid rgba(56, 249, 215, 0.2)"
                      }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Detailed Skills Section */}
        <h2 style={{ 
          color: "#c770f0", 
          textAlign: "center", 
          marginBottom: "30px", 
          fontSize: "1.8em",
          fontWeight: "600"
        }}>
          Detailed Skills & Proficiency
        </h2>

        <Row style={{ justifyContent: "center", paddingBottom: "10px" }}>
          {skillCategories.map((category, index) => (
            <Col md={6} lg={4} className="skill-category" key={index} style={{ marginBottom: "40px" }}>
              <div
                className="skill-category-view"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "20px",
                  padding: "35px",
                  color: "white",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-3px) scale(1.01)";
                  e.target.style.boxShadow = "0 12px 25px rgba(199, 112, 240, 0.2)";
                  e.target.style.borderColor = "rgba(199, 112, 240, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0) scale(1)";
                  e.target.style.boxShadow = "0 8px 20px rgba(0, 0, 0, 0.15)";
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "25px"
                }}>
                  <div style={{
                    fontSize: "2em",
                    color: "#c770f0",
                    marginRight: "15px"
                  }}>
                    {category.icon}
                  </div>
                  <h3 style={{
                    fontSize: "1.4em",
                    fontWeight: "600",
                    margin: 0,
                    color: "#c770f0"
                  }}>
                    {category.title}
                  </h3>
                </div>

                <div style={{ space: "20px" }}>
                  {category.skills.map((skill, skillIndex) => (
                    <div key={skillIndex} style={{ marginBottom: "20px" }}>
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px"
                      }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <span style={{
                            color: "#c770f0",
                            fontSize: "0.8em"
                          }}>
                            ▸
                          </span>
                          <span style={{
                            fontSize: "1em",
                            fontWeight: "500"
                          }}>
                            {skill.name}
                          </span>
                        </div>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <span style={{
                            fontSize: "0.8em",
                            color: "rgba(255, 255, 255, 0.7)"
                          }}>
                            {skill.level}
                          </span>
                          <span style={{
                            fontSize: "0.9em",
                            fontWeight: "600",
                            color: "#38f9d7"
                          }}>
                            {skill.percentage}%
                          </span>
                        </div>
                      </div>
                      <ProgressBar percentage={skill.percentage} />
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Container>
    </Container>
  );
}

export default TechnicalSkills;
